/*
 * Product media gallery drag control.
 *
 * Dragging the main image pulls the neighbouring photo in behind the pointer,
 * and on release the gallery slides the rest of the way to whichever photo the
 * drag committed to.
 *
 * Below 750px the theme already ships a scroll-snap slider, and touch keeps
 * using it natively. Above 750px the theme shows a single slide and hides the
 * rest, so the list is laid out as a track by the .gallery-drag-track rules in
 * 'product-media-gallery.liquid' -- added from here, so the layout only changes
 * when this script is actually running.
 */
if (!window.galleryDragNavigation) {
  window.galleryDragNavigation = true;

  const TRACK_CLASS = 'gallery-drag-track';
  // Share of the viewer the pointer has to cross to commit to the next photo.
  const COMMIT_RATIO = 0.15;
  const COMMIT_MAX = 80;
  // Long enough to read as a slide rather than a cut.
  const SLIDE_MS = 320;

  const drag = {
    pointerId: null,
    gallery: null,
    list: null,
    startX: 0,
    startY: 0,
    startScroll: 0,
    startIndex: 0,
    moved: false,
    interrupted: false,
  };

  let animation = null;

  // Video and 3D players own their pointer gestures, so a drag that starts on
  // one is theirs to handle, not a swipe.
  const isPlayer = (element) => element.closest('.deferred-media, model-viewer, video, iframe');

  // Match the slide wrappers only. A plain [data-media-id] lookup also picks up
  // the lightbox opener nested in every slide, which doubles the list and makes
  // the gesture land on ids the thumbnails know nothing about.
  const getItems = (gallery) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    return viewer ? Array.from(viewer.querySelectorAll('.product__media-item')) : [];
  };

  const isSlider = (list) => list.scrollWidth > list.clientWidth + 1;

  const indexAt = (list, items, scrollLeft) => {
    const middle = scrollLeft + list.clientWidth / 2;
    return items.reduce(
      (closest, item, index) => {
        const distance = Math.abs(item.offsetLeft + item.clientWidth / 2 - middle);
        return distance < closest.distance ? { index: index, distance: distance } : closest;
      },
      { index: 0, distance: Infinity }
    ).index;
  };

  // Every slide but the first is lazy, and sitting outside the scrollport is
  // not close enough for the browser to fetch one on its own. A drag reveals
  // the neighbour immediately, so it has to already be on its way.
  const warmNeighbours = (items, index) => {
    [index - 1, index + 1].forEach((neighbour) => {
      if (!items[neighbour]) return;
      items[neighbour].querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
    });
  };

  // The theme's setActiveMedia scrolls the track a second time and can pull the
  // whole page upwards when the photo's top sits above the fold -- both of them
  // land as a jump right after a drag. So the same bookkeeping is done here
  // through its own helpers, minus the scrolling.
  const syncGallery = (gallery, items, target) => {
    items.forEach((item) => item.classList.toggle('is-active', item === target));

    const thumbnails = gallery.elements && gallery.elements.thumbnails;
    const thumbnail =
      thumbnails && thumbnails.querySelector(`[data-target="${target.dataset.mediaId}"]`);

    if (thumbnail && typeof gallery.setActiveThumbnail === 'function') {
      gallery.setActiveThumbnail(thumbnail);
    }
    if (typeof gallery.playActiveMedia === 'function') {
      gallery.playActiveMedia(target);
    }
    if (thumbnail && typeof gallery.announceLiveRegion === 'function') {
      gallery.announceLiveRegion(target, thumbnail.dataset.mediaPosition);
    }
  };

  // Leaves the track wherever it currently sits: a drag that interrupts a slide
  // has to take over from there, not from where the slide was headed.
  const stopAnimation = () => {
    if (!animation) return false;
    cancelAnimationFrame(animation.frame);
    animation = null;
    return true;
  };

  const restoreScrolling = (list) => {
    list.style.scrollSnapType = '';
    list.style.scrollBehavior = '';
  };

  const animateTo = (list, to, done) => {
    stopAnimation();

    const from = list.scrollLeft;
    const distance = to - from;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (still || Math.abs(distance) < 1) {
      list.scrollLeft = to;
      done();
      return;
    }

    const started = performance.now();
    const step = (now) => {
      if (!animation) return;

      const progress = Math.min((now - started) / SLIDE_MS, 1);
      // easeOutCubic, so the photo arrives gently instead of stopping dead.
      list.scrollLeft = from + distance * (1 - Math.pow(1 - progress, 3));

      if (progress < 1) {
        animation.frame = requestAnimationFrame(step);
        return;
      }
      animation = null;
      done();
    };

    animation = { frame: requestAnimationFrame(step), list: list };
  };

  document.addEventListener('pointerdown', (event) => {
    if (drag.pointerId !== null || event.button !== 0) return;
    if (!(event.target instanceof Element) || isPlayer(event.target)) return;
    // Touch scrolls the slider natively, so only take over the pointers the
    // browser gives no drag behaviour to at all.
    if (event.pointerType === 'touch') return;

    const gallery = event.target.closest('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const list = event.target.closest('[id^="GalleryViewer"] .product__media-list');
    // Nothing to drag through unless the viewer scrolls, which leaves
    // single-image products and the stacked and columns layouts alone.
    if (!list || !isSlider(list)) return;

    const items = getItems(gallery);
    drag.interrupted = stopAnimation();
    drag.pointerId = event.pointerId;
    drag.gallery = gallery;
    drag.list = list;
    drag.startX = event.clientX;
    drag.startY = event.clientY;
    drag.startScroll = list.scrollLeft;
    drag.startIndex = indexAt(list, items, list.scrollLeft);
    drag.moved = false;
    warmNeighbours(items, drag.startIndex);
    list.setPointerCapture(event.pointerId);
  });

  document.addEventListener('pointermove', (event) => {
    if (event.pointerId !== drag.pointerId) return;

    const distanceX = event.clientX - drag.startX;
    if (!drag.moved) {
      // Wait until the gesture is clearly horizontal: a vertical drag is the
      // shopper scrolling the page past the gallery.
      if (Math.abs(distanceX) < 5 || Math.abs(distanceX) <= Math.abs(event.clientY - drag.startY)) {
        return;
      }
      drag.moved = true;
      // Snapping would fight the drag a step at a time and smooth scrolling
      // would trail the pointer. Both come back once the slide has landed.
      drag.list.style.scrollSnapType = 'none';
      drag.list.style.scrollBehavior = 'auto';
      // Without this the drag paints a text selection across the page.
      drag.list.style.userSelect = 'none';
      drag.list.classList.add('gallery-dragging');
    }

    drag.list.scrollLeft = drag.startScroll - distanceX;
  });

  const endDrag = (event, commit) => {
    if (event.pointerId !== drag.pointerId) return;

    const distanceX = event.clientX - drag.startX;
    const gallery = drag.gallery;
    const list = drag.list;
    const startIndex = drag.startIndex;
    const dragged = drag.moved;
    const interrupted = drag.interrupted;

    list.style.userSelect = '';
    list.classList.remove('gallery-dragging');
    drag.pointerId = null;
    drag.gallery = null;
    drag.list = null;
    drag.moved = false;
    drag.interrupted = false;

    const items = getItems(gallery);
    const threshold = Math.min(list.clientWidth * COMMIT_RATIO, COMMIT_MAX);
    const committed = dragged && commit && Math.abs(distanceX) > threshold;
    // Short of the threshold the track goes back to the photo nearest to where
    // it was let go, which is also how a tap stops a slide mid-flight.
    const index = committed
      ? Math.min(Math.max(startIndex + (distanceX < 0 ? 1 : -1), 0), items.length - 1)
      : indexAt(list, items, list.scrollLeft);
    const target = items[index];

    if (dragged) {
      // Swallow the click the browser fires after the drag, so the lightbox
      // doesn't open on top of the photo the shopper just swiped to.
      const swallowClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      list.addEventListener('click', swallowClick, { capture: true, once: true });
      setTimeout(() => list.removeEventListener('click', swallowClick, { capture: true }));
    }

    if (!target) {
      restoreScrolling(list);
      return;
    }

    // A plain click on the photo it is already resting on has nothing to settle.
    if (!dragged && !interrupted && target.classList.contains('is-active')) {
      restoreScrolling(list);
      return;
    }

    animateTo(list, target.offsetLeft, () => {
      restoreScrolling(list);
      syncGallery(gallery, items, target);
      warmNeighbours(items, index);
    });
  };

  document.addEventListener('pointerup', (event) => endDrag(event, true));
  document.addEventListener('pointercancel', (event) => endDrag(event, false));
  // A native image drag would otherwise cancel the gesture halfway through.
  document.addEventListener('dragstart', (event) => {
    if (drag.pointerId !== null) event.preventDefault();
  });

  // Another gallery control -- a thumbnail hover, say -- is taking the viewer
  // over while a slide is still travelling.
  document.addEventListener('gallery:interrupt', () => {
    const list = animation && animation.list;
    if (stopAnimation() && list) restoreScrolling(list);
  });

  const layOutTracks = () => {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const list = gallery.querySelector('[id^="GalleryViewer"] .product__media-list');
      if (!list) return;
      list.classList.add(TRACK_CLASS);
      const items = getItems(gallery);
      warmNeighbours(items, indexAt(list, items, list.scrollLeft));
    });
  };

  layOutTracks();

  // Variant switches and quick-add drawers render galleries of their own.
  let queued = false;
  new MutationObserver((records) => {
    if (queued || !records.some((record) => record.addedNodes.length)) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      layOutTracks();
    });
  }).observe(document.body, { childList: true, subtree: true });
}
